import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const IS_DEMO = (() => { try { return /^\/demo(\/|$)/.test(window.location.pathname); } catch { return false; } })();
// Domain-ready SaaS marketing site: hosts listed here serve the marketing homepage at "/".
// Empty (default) => behaves exactly as before (Prime Gym Turf storefront at "/").
const _saasHosts = (process.env.REACT_APP_SAAS_HOSTS || "").split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
export const IS_SAAS_HOST = (() => { try { return _saasHosts.includes(window.location.hostname.toLowerCase()); } catch { return false; } })();
const _pathSlug = (() => {
  try {
    if (IS_DEMO) return "demo-turf-designer";
    const m = window.location.pathname.match(/^\/(?:embed|t)\/([^/?#]+)/); return m ? decodeURIComponent(m[1]) : "prime-gym-turf";
  } catch { return "prime-gym-turf"; }
})();
export const SLUG = _pathSlug;
export const IS_EMBED = (() => { try { return /^\/(embed|t)\//.test(window.location.pathname); } catch { return false; } })();

export const http = axios.create({ baseURL: API });

const TOKEN_KEY = "pgt_admin_token";
export const setToken = (t) => { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); };
export const getToken = () => localStorage.getItem(TOKEN_KEY);
http.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export const fileUrl = (fileId) => `${API}/files/${fileId}`;

export const getTenantConfig = () => http.get(`/tenant/config`, { params: { slug: SLUG } }).then((r) => r.data);
export const getGeo = () => http.get(`/geo`).then((r) => r.data);
export const adminFxRefresh = () => http.post(`/admin/fx/refresh`).then((r) => r.data);

export const calcPrice = (payload) => http.post(`/price/calculate`, { slug: SLUG, ...payload }).then((r) => r.data);

export const uploadArtwork = (file) => {
  const fd = new FormData();
  fd.append("slug", SLUG);
  fd.append("file", file);
  return http.post(`/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

export const saveDesign = (payload) => http.post(`/designs`, { slug: SLUG, origin_url: window.location.origin, ...payload }).then((r) => r.data);
export const getDesign = (id) => http.get(`/designs/${id}`).then((r) => r.data);

export const trackEvent = (event, design_id, meta = {}) =>
  http.post(`/events`, { slug: SLUG, event, design_id, meta }).catch(() => {});

export const createCheckout = (payload) => http.post(`/checkout`, { slug: SLUG, origin_url: window.location.origin, ...payload }).then((r) => r.data);
export const shopifyCheckout = (payload) => http.post(`/shopify/checkout`, { slug: SLUG, origin_url: window.location.origin, ...payload }).then((r) => r.data);
export const paymentStatus = (sid) => http.get(`/payments/status/${sid}`).then((r) => r.data);
export const getOrder = (num, token) => http.get(`/orders/${num}`, { params: { token } }).then((r) => r.data);
export const approveArtwork = (num, token) => http.post(`/orders/${num}/approve`, null, { params: { token } }).then((r) => r.data);

// auth
export const authSession = (session_id) => http.post(`/auth/session`, { session_id }).then((r) => { if (r.data.session_token) setToken(r.data.session_token); return r.data; });
export const authMe = () => http.get(`/auth/me`).then((r) => r.data);
export const authLogout = () => http.post(`/auth/logout`).then((r) => r.data).finally(() => setToken(null));

// admin
export const adminOverview = () => http.get(`/admin/overview`).then((r) => r.data);
export const adminOrders = (status) => http.get(`/admin/orders`, { params: { status } }).then((r) => r.data);
export const adminUpdateStatus = (num, body) => http.patch(`/admin/orders/${num}/status`, body).then((r) => r.data);
export const adminSendProof = (num) => http.post(`/admin/orders/${num}/send-proof`).then((r) => r.data);
export const adminDesigns = () => http.get(`/admin/designs`).then((r) => r.data);
export const adminGetSettings = () => http.get(`/admin/settings`).then((r) => r.data);
export const adminPutSettings = (body) => http.put(`/admin/settings`, body).then((r) => r.data);
export const adminAiDigestTest = () => http.post(`/admin/ai-digest/test`).then((r) => r.data);
export const adminGetAiPrompts = () => http.get(`/admin/ai-prompts`).then((r) => r.data);
export const adminPutAiPrompts = (body) => http.put(`/admin/ai-prompts`, body).then((r) => r.data);
export const adminEffectivePrompts = () => http.get(`/admin/ai-prompts/effective`).then((r) => r.data);

// ---- Phase 3: SaaS auth / platform / tenant dashboard ----
export const authLogin = (email, password) => http.post(`/auth/login`, { email, password }).then((r) => { if (r.data.session_token) setToken(r.data.session_token); return r.data; });
export const authRegister = (b) => http.post(`/auth/register`, b).then((r) => { if (r.data.session_token) setToken(r.data.session_token); return r.data; });
export const authSignup = (b) => http.post(`/auth/signup`, b).then((r) => { if (r.data.session_token) setToken(r.data.session_token); return r.data; });
export const gettingStarted = () => http.get(`/tenant/getting-started`).then((r) => r.data);
export const setOnboarding = (b) => http.patch(`/admin/onboarding`, b).then((r) => r.data);
export const platformTrials = () => http.get(`/platform/trials`).then((r) => r.data);
export const platformAnalytics = () => http.get(`/platform/analytics`).then((r) => r.data);
export const tenantAnalytics = () => http.get(`/tenant/analytics`).then((r) => r.data);
export const trackActivation = (event) => http.post(`/tenant/activation`, { event }).then((r) => r.data).catch(() => {});
export const authForgot = (email) => http.post(`/auth/forgot`, { email, base_url: window.location.origin }).then((r) => r.data);
export const authReset = (token, password) => http.post(`/auth/reset`, { token, password }).then((r) => r.data);
export const authSetPassword = (password) => http.post(`/auth/set-password`, { password }).then((r) => r.data);
export const authUpdateAccount = (name) => http.patch(`/auth/account`, { name }).then((r) => r.data);
export const getInvite = (token) => http.get(`/invites/${token}`).then((r) => r.data);
export const acceptInvite = (token, body) => http.post(`/invites/${token}/accept`, body).then((r) => { if (r.data.session_token) setToken(r.data.session_token); return r.data; });
export const platformTenants = () => http.get(`/platform/tenants`).then((r) => r.data);
export const platformGetTenant = (slug) => http.get(`/platform/tenants/${slug}`).then((r) => r.data);
export const platformCreateTenant = (b) => http.post(`/platform/tenants`, { ...b, base_url: window.location.origin }).then((r) => r.data);
export const platformPatchTenant = (slug, b) => http.patch(`/platform/tenants/${slug}`, b).then((r) => r.data);
export const platformAudit = () => http.get(`/platform/audit`).then((r) => r.data);
export const tenantDashboard = () => http.get(`/tenant/dashboard`).then((r) => r.data);
export const tenantBranding = () => http.get(`/tenant/branding`).then((r) => r.data);
export const updateBranding = (b) => http.patch(`/admin/branding`, b).then((r) => r.data);
export const listMembers = () => http.get(`/admin/members`).then((r) => r.data);
export const teamInvite = (email, role) => http.post(`/admin/team/invite`, { email, role, base_url: window.location.origin }).then((r) => r.data);
export const removeMember = (user_id) => http.delete(`/admin/members/${user_id}`).then((r) => r.data);
export const adminPreviewAiPrompt = (body) => http.post(`/admin/ai-prompts/preview`, body).then((r) => r.data);
export const adminList = (kind) => http.get(`/admin/${kind}`).then((r) => r.data);
export const adminCreate = (kind, body) => http.post(`/admin/${kind}`, body).then((r) => r.data);
export const adminUpdate = (kind, id, body) => http.patch(`/admin/${kind}/${id}`, body).then((r) => r.data);
export const adminDelete = (kind, id) => http.delete(`/admin/${kind}/${id}`).then((r) => r.data);
export const productionPackUrl = (num) => `${API}/admin/orders/${num}/production-pack.pdf`;
export const artworkSvgUrl = (num, safe = 0) => `${API}/admin/orders/${num}/artwork.svg?safe=${safe}`;

// ---- Phase 4: billing / subscriptions / AI credits ----
export const billingSummary = () => http.get(`/billing/summary`).then((r) => r.data);
export const billingSubscribe = (plan_id, interval = "monthly") => http.post(`/billing/subscribe`, { plan_id, interval, origin_url: window.location.origin }).then((r) => r.data);
export const billingBuyCredits = (pack_id) => http.post(`/billing/buy-credits`, { pack_id, origin_url: window.location.origin }).then((r) => r.data);
export const billingPortal = () => http.post(`/billing/portal`, { origin_url: window.location.origin }).then((r) => r.data);
export const billingReturn = (sid) => http.get(`/billing/return/${sid}`).then((r) => r.data);
export const platformBilling = () => http.get(`/platform/billing`).then((r) => r.data);
export const platformPlans = () => http.get(`/platform/plans`).then((r) => r.data);
export const platformPacks = () => http.get(`/platform/packs`).then((r) => r.data);
export const platformCreatePlan = (b) => http.post(`/platform/plans`, b).then((r) => r.data);
export const platformUpdatePlan = (plan_id, b) => http.patch(`/platform/plans/${plan_id}`, b).then((r) => r.data);
export const platformCreatePack = (b) => http.post(`/platform/packs`, b).then((r) => r.data);
export const platformUpdatePack = (pack_id, b) => http.patch(`/platform/packs/${pack_id}`, b).then((r) => r.data);
export const platformStripeSync = () => http.post(`/platform/stripe/sync`).then((r) => r.data);

// ---- Phase 5: embed distribution + design leads ----
export const submitLead = (payload) => http.post(`/leads`, { slug: SLUG, origin_url: window.location.href, ...payload }).then((r) => r.data);
export const tenantEmbed = () => http.get(`/tenant/embed`).then((r) => r.data);
export const updateEmbed = (b) => http.patch(`/admin/embed`, b).then((r) => r.data);
export const tenantLeads = () => http.get(`/tenant/leads`).then((r) => r.data);
export const updateLeadStatus = (lead_id, status) => http.patch(`/leads/${lead_id}`, { status }).then((r) => r.data);
export const updateNotifications = (payload) => http.patch(`/tenant/notifications`, payload).then((r) => r.data);

// ---- Phase 9: marketing site + demo funnel ----
export const trackMarketing = (event, meta = {}) => http.post(`/events`, { slug: "marketing", event, meta }).catch(() => {});
export const submitContact = (payload) => http.post(`/contact`, payload).then((r) => r.data);
export const platformMarketing = () => http.get(`/platform/marketing`).then((r) => r.data);

// AI Design My Turf
export const aiAnalyzeLogo = (image_base64) => http.post(`/ai/analyze-logo`, { image_base64 }).then((r) => r.data);
export const aiRemoveBg = (image_base64) => http.post(`/ai/remove-bg`, { image_base64 }).then((r) => r.data);
export const aiGenerate = (payload) => http.post(`/ai/generate`, { slug: SLUG, ...payload }).then((r) => r.data);
export const aiGetSession = (sid) => http.get(`/ai/session/${sid}`).then((r) => r.data);
export const aiSelectConcept = (payload) => http.post(`/ai/select`, { slug: SLUG, ...payload }).then((r) => r.data);
export const aiEditArtwork = (payload) => http.post(`/ai/edit`, { slug: SLUG, ...payload }).then((r) => r.data);
export const aiGetJob = (jobId) => http.get(`/ai/job/${jobId}`).then((r) => r.data);

// Poll an async AI job until it completes/fails. `onTick` receives the raw job each poll.
// Resolves with the completed job.result (or throws on failure/timeout).
export const aiPollJob = (jobId, { interval = 2500, timeout = 120000, signal, onTick } = {}) =>
  new Promise((resolve, reject) => {
    const start = Date.now();
    let stopped = false;
    if (signal) signal.addEventListener("abort", () => { stopped = true; reject(new Error("aborted")); });
    const tick = async () => {
      if (stopped) return;
      try {
        const job = await aiGetJob(jobId);
        if (onTick) onTick(job);
        if (job.status === "completed") return resolve(job.result);
        if (job.status === "failed") return reject(new Error(job.error || "generation failed"));
        if (Date.now() - start > timeout) { const e = new Error("timeout"); e.code = "timeout"; return reject(e); }
      } catch (e) {
        if (e?.response?.status === 404) return reject(e); // job expired / missing
        // transient network error: keep polling until timeout
        if (Date.now() - start > timeout) { const t = new Error("timeout"); t.code = "timeout"; return reject(t); }
      }
      setTimeout(tick, interval);
    };
    tick();
  });
